import { useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Clock, MapPin, CalendarIcon, AlertCircle, Camera } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { JobPhotoManager } from "@/components/JobPhotoManager";

// Utility function to extract postcode from UK address
const extractPostcode = (address: string): string => {
  if (!address) return 'Location not specified';
  
  // Split by comma and trim each part
  const parts = address.split(',').map(part => part.trim());
  
  // UK postcode pattern: Letters/numbers followed by space and 3 characters
  const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
  
  // Look for postcode in each part
  for (const part of parts) {
    if (postcodeRegex.test(part)) {
      return part;
    }
  }
  
  // Fallback: take last 2 parts after comma split (city, postcode area)
  if (parts.length >= 2) {
    return parts.slice(-2).join(', ');
  }
  
  return address;
};

interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  priority: string;
  status?: string;
  budget_min?: number;
  budget_max?: number;
  response_by: string;
  required_date?: string;
}

interface RespondToJobDialogProps {
  job: Job | null;
  onClose: () => void;
  onJobResponded: () => void;
}

export const RespondToJobDialog = ({ job, onClose, onJobResponded }: RespondToJobDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [counterOfferDate, setCounterOfferDate] = useState<Date>();
  const [counterOfferTime, setCounterOfferTime] = useState("");
  const [acceptCustomerTime, setAcceptCustomerTime] = useState(true);
  const [showJobPhotos, setShowJobPhotos] = useState(false);
  const { toast } = useToast();

  const handleNotInterested = async () => {
    if (!job) return;
    
    setIsLoading(true);
    
    try {
      // Get the tradesman's profile
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id, company_name")
        .eq("user_id", user.user.id)
        .single();

      if (!profile) throw new Error("Profile not found");

      // Get job details to find customer
      const { data: jobData } = await supabase
        .from("jobs")
        .select("customer_id")
        .eq("id", job.id)
        .single();

      if (!jobData) throw new Error("Job not found");

      // Send message to customer via the chat system
      const { error: messageError } = await supabase.functions.invoke('send-message', {
        body: {
          job_id: job.id,
          recipient_id: jobData.customer_id,
          content: `Thank you for considering ${profile.company_name || 'us'} for your job "${job.title}". Unfortunately, we are not available to quote for this project at this time. We appreciate your understanding and wish you the best of luck with your project.`,
          message_type: 'text'
        }
      });

      if (messageError) throw messageError;

      toast({
        title: "Customer notified",
        description: "The customer has been informed that you're not interested in this job.",
      });

      onJobResponded();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error sending notification",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!job) return;
    
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const message = formData.get("message") as string;
    const estimatedCost = formData.get("estimatedCost") as string;
    const estimatedDuration = formData.get("estimatedDuration") as string;

    // Determine availability based on customer time acceptance or counter-offer
    let availability = null;
    if (acceptCustomerTime && job.required_date) {
      availability = job.required_date;
    } else if (!acceptCustomerTime && counterOfferDate && counterOfferTime) {
      const [hours, minutes] = counterOfferTime.split(':');
      const combinedDate = new Date(counterOfferDate);
      combinedDate.setHours(parseInt(hours), parseInt(minutes));
      availability = combinedDate.toISOString();
    }

    try {
      // Get the tradesman's profile
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id, insurance_approved")
        .eq("user_id", user.user.id)
        .single();

      if (!profile) throw new Error("Profile not found");
      if (!profile.insurance_approved) throw new Error("Insurance approval required to respond to jobs");

      // Check if job is already assigned to prevent quote changes
      if (job.status && ['quote_accepted', 'assigned', 'in_progress', 'completed'].includes(job.status)) {
        throw new Error("Cannot modify quote after job has been assigned");
      }

      // Submit response via secure RPC (bypasses RLS with strict checks)
      const { data: rpcResponseId, error: rpcError } = await supabase.rpc('submit_job_response_safe', {
        p_job_id: job.id,
        p_message: message,
        p_estimated_cost: estimatedCost ? parseFloat(estimatedCost) : null,
        p_estimated_duration: estimatedDuration || null,
        p_availability: availability,
      });

      if (rpcError) throw rpcError;

      const responseId = rpcResponseId as unknown as string;
      if (!responseId) throw new Error('Failed to save response');

      

      // Rely on DB trigger to set job status to 'quoted' when estimated_cost is provided
      if (estimatedCost) {
        console.log('Quote includes estimated cost; DB trigger will set job status to quoted if applicable');
      } else {
        console.log('No estimated cost provided, not updating job status');
      }


      // Notify the customer about the new quote
      try {
        const { error: notificationError } = await supabase.functions.invoke('notify-customer-quote', {
          body: {
            jobId: job.id,
            responseId: responseId
          }
        });

        if (notificationError) {
          console.error('Failed to send customer notification:', notificationError);
        } else {
          console.log('Customer notified about new quote');
        }
      } catch (notificationError) {
        console.error('Error sending customer notification:', notificationError);
        // Don't fail the response submission if notification fails
      }

      toast({
        title: "Response submitted successfully!",
        description: "The customer has been notified and will review your response.",
      });

      onJobResponded();
      onClose();
      setCounterOfferDate(undefined);
      setCounterOfferTime("");
      setAcceptCustomerTime(true);
      (e.target as HTMLFormElement).reset();
    } catch (error: any) {
      toast({
        title: "Error submitting response",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "emergency": return "bg-red-100 text-red-800";
      case "urgent": return "bg-orange-100 text-orange-800";
      case "standard": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const formatBudget = (min?: number, max?: number) => {
    if (!min && !max) return "Budget not specified";
    if (min && max) return `£${min} - £${max}`;
    if (min) return `From £${min}`;
    if (max) return `Up to £${max}`;
    return "";
  };

  if (!job) return null;

  return (
    <Dialog open={!!job} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-lg sm:text-xl">Submit Your Quote</DialogTitle>
          <DialogDescription className="text-sm">
            Submit your quote for this job request. The customer expects quotes promptly.
          </DialogDescription>
        </DialogHeader>

        {/* Job Details */}
        <div className="bg-muted p-3 sm:p-4 rounded-lg mb-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
            <h3 className="font-semibold text-base sm:text-lg flex-1">{job.title}</h3>
            <div className="flex items-center gap-2 self-start sm:self-auto">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowJobPhotos(true)}
                className="h-9 w-9 p-0"
              >
                <Camera className="h-4 w-4" />
              </Button>
              <Badge className={getPriorityColor(job.priority)} variant="secondary">
                {job.priority}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-1 text-muted-foreground text-sm">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="break-all">{extractPostcode(job.location)}</span>
          </div>
          
          <p className="text-sm text-muted-foreground leading-relaxed">{job.description}</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
            <div className="space-y-1">
              <span className="text-muted-foreground block">Customer Budget:</span>
              <div className="font-medium text-base">{formatBudget(job.budget_min, job.budget_max)}</div>
            </div>
            <div className="space-y-1">
              <span className="text-muted-foreground block">Response by:</span>
              <div className="flex items-center gap-1 text-primary font-medium text-base">
                <Clock className="h-4 w-4" />
                {new Date(job.response_by).toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>
          </div>

          {/* Customer's preferred time */}
          {job.required_date && (
            <div className="mt-3 p-3 bg-accent/50 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="font-medium text-primary">Customer's Preferred Time:</span>
                </div>
                <span className="font-medium sm:ml-0">
                  {format(new Date(job.required_date), "PPP 'at' p")}
                </span>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
          <div className="space-y-2">
            <Label htmlFor="message" className="text-sm sm:text-base">Your Message</Label>
            <Textarea
              id="message"
              name="message"
              placeholder="Introduce yourself and explain how you can help with this job..."
              rows={4}
              required
              className="min-h-[100px] text-base"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="estimatedCost" className="text-sm sm:text-base">Your Quote (£)</Label>
              <Input
                id="estimatedCost"
                name="estimatedCost"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g., 150"
                className="h-11 text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="estimatedDuration" className="text-sm sm:text-base">Estimated Duration</Label>
              <Input
                id="estimatedDuration"
                name="estimatedDuration"
                placeholder="e.g., 2-3 hours, 1 day"
                className="h-11 text-base"
              />
            </div>
          </div>

          {/* Availability Section */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary flex-shrink-0" />
              <Label className="text-sm sm:text-base font-medium">Availability</Label>
            </div>

            {job.required_date ? (
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    id="accept-time"
                    name="time-preference"
                    checked={acceptCustomerTime}
                    onChange={() => setAcceptCustomerTime(true)}
                    className="w-5 h-5 text-primary mt-0.5 flex-shrink-0"
                  />
                  <Label htmlFor="accept-time" className="flex-1 cursor-pointer">
                    <span className="font-medium text-green-700 text-base">✓ I can work at the customer's preferred time</span>
                    <div className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                      {format(new Date(job.required_date), "EEEE, MMMM do 'at' h:mm a")}
                    </div>
                  </Label>
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    id="counter-offer"
                    name="time-preference"
                    checked={!acceptCustomerTime}
                    onChange={() => setAcceptCustomerTime(false)}
                    className="w-5 h-5 text-primary mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1">
                    <Label htmlFor="counter-offer" className="font-medium text-accent-orange text-base cursor-pointer">
                      I'd like to propose a different time
                    </Label>
                    
                    {!acceptCustomerTime && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm">Proposed Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full h-11 justify-start text-left font-normal text-base",
                                  !counterOfferDate && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                                {counterOfferDate ? format(counterOfferDate, "PPP") : <span>Pick a date</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={counterOfferDate}
                                onSelect={setCounterOfferDate}
                                disabled={(date) => date < new Date()}
                                initialFocus
                                className={cn("p-3 pointer-events-auto")}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-sm">Proposed Time</Label>
                          <Input
                            type="time"
                            value={counterOfferTime}
                            onChange={(e) => setCounterOfferTime(e.target.value)}
                            className="w-full h-11 text-base"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    Customer hasn't specified a preferred time. Please suggest your earliest availability.
                  </span>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm">Available Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full h-11 justify-start text-left font-normal text-base",
                            !counterOfferDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                          {counterOfferDate ? format(counterOfferDate, "PPP") : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={counterOfferDate}
                          onSelect={setCounterOfferDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Available Time</Label>
                    <Input
                      type="time"
                      value={counterOfferTime}
                      onChange={(e) => setCounterOfferTime(e.target.value)}
                      className="w-full h-11 text-base"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto h-11 text-base">
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="destructive" 
              onClick={handleNotInterested}
              disabled={isLoading}
              className="w-full sm:w-auto h-11 text-base"
            >
              {isLoading ? "Sending..." : "Not Interested"}
            </Button>
            <Button type="submit" disabled={isLoading} variant="orange" className="w-full sm:flex-1 h-11 text-base font-semibold">
              {isLoading ? "Submitting..." : "Submit Your Quote"}
            </Button>
          </div>
        </form>
      </DialogContent>

      <JobPhotoManager
        jobId={job?.id || ""}
        open={showJobPhotos}
        onOpenChange={setShowJobPhotos}
        isCustomer={false}
      />
    </Dialog>
  );
};